import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { SectionHeader, useFocusHighlight, ConfirmDialog } from "../components/shared";
import { itemColumns } from "../components/itemColumns";
import { CsvTools } from "../components/CsvTools";
import { Button } from "../components/ui/button";
import { DataTable } from "../components/DataTable";
import { ITEM_EXTRA } from "../lib/extraColumns";
import { usePerms } from "../lib/perms";

export default function Inventory() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { rowProps } = useFocusHighlight();
  const { can } = usePerms();
  const canEdit = can("items.edit");
  const [delItem, setDelItem] = useState(null);

  const { data: items = [], isLoading } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const vendorCode = Object.fromEntries(vendors.map((v) => [v.id, v.code || v.name]));

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/items/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["items"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Item deleted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div className="animate-fade-up">
      <SectionHeader title="Items" subtitle="Stock management">
        <div className="flex items-center gap-2 flex-wrap">
          <CsvTools
            entity="items"
            queryKey={["items"]}
            label="items"
            rows={items}
            columns={[
              { key: "sku", label: "sku" },
              { key: "name", label: "name" },
              { key: "description", label: "description" },
              { key: "category", label: "category" },
              { key: "unit_of_measure", label: "unit_of_measure" },
              { key: "unit_price", label: "unit_price" },
              { key: "cost", label: "cost" },
              { key: "quantity", label: "quantity" },
              { key: "reorder_point", label: "reorder_point" },
              { key: "location_code", label: "location_code" },
              { key: "vendor_code", label: "vendor_code", value: (r) => vendorCode[r.vendor_id] || "" },
              { key: "lead_time_days", label: "lead_time_days" },
              { key: "vendor_part_no", label: "vendor_part_no" },
              { key: "quote_ref_no", label: "quote_ref_no" },
            ]}
          />
          {canEdit && <Button onClick={() => navigate("/inventory/item/new")} data-testid="new-item-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9">
            <Plus size={16} weight="bold" className="mr-1" /> New Item
          </Button>}
        </div>
      </SectionHeader>

      <DataTable
        testid="items-table"
        viewKey="items" namedViews extraColumns={ITEM_EXTRA}
        data={items}
        searchPlaceholder="Search items…"
        loading={isLoading}
        rowKey={(i) => i.id}
        rowTestid="item-row"
        rowProps={(i) => rowProps(i.id)}
        empty="No items found."
        columns={[
          ...itemColumns((i) => navigate(`/inventory/item/${i.id}`)),
          { key: "actions", header: "Actions", align: "right", sortable: false, cell: (i) => canEdit ? <button onClick={() => setDelItem(i)} data-testid="delete-item" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash size={16} /></button> : null },
        ]}
      />

      <ConfirmDialog
        open={!!delItem}
        onOpenChange={(v) => { if (!v) setDelItem(null); }}
        title="Delete this item?"
        description={delItem ? `${delItem.name} will be permanently deleted.` : ""}
        confirmLabel="Delete"
        testid="item-delete-confirm"
        onConfirm={() => { if (delItem) del.mutate(delItem.id); setDelItem(null); }}
      />
    </div>
  );
}
